// src/pages/ReverifyID.tsx
import { useState, ChangeEvent, FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authFetch } from "../lib/auth";

export default function ReverifyID() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const returnTo = params.get("returnTo") || params.get("next") || "/contracts/new";

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    setErr(null);
    setMessage(null);
    const f = e.target.files?.[0] || null;
    setFile(f);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) {
      setErr("신분증 이미지를 선택해 주세요.");
      return;
    }

    setErr(null);
    setMessage(null);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("idcard", file);

      const res = await authFetch("/api/ekyc/idcard", {
        method: "POST",
        body: fd,
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(json.message || "신분증 인증에 실패했습니다.");
      }

      setMessage("인증이 완료되어 이전 단계로 이동합니다.");

      const baseDest = returnTo === "/onboarding" ? "/" : returnTo;

      setTimeout(() => {
        try {
          const url = new URL(baseDest, window.location.origin);
          url.searchParams.set("session_ekyc", "ok");
          navigate(url.pathname + url.search, { replace: true });
        } catch {
          const sep = baseDest.includes("?") ? "&" : "?";
          navigate(baseDest + sep + "session_ekyc=ok", { replace: true });
        }
      }, 1500);
    } catch (e: any) {
      console.error("/api/ekyc/idcard error", e);
      setErr(e?.message || "신분증 인증 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    const dest = returnTo || "/";
    navigate(dest, { replace: true });
  };

  return (
    <div className="min-h-[100dvh] bg-[#f4f8ff] px-4 py-6 sm:px-6 sm:py-10">
      <main className="mx-auto max-w-xl">
        <div className="mb-5 rounded-[30px] border border-[#dbe6fb] bg-white/95 p-5 shadow-[0_20px_45px_rgba(61,107,219,0.12)] sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#5d7bc6]">Identity Recheck</div>
              <h1 className="mt-2 text-[28px] font-black tracking-[-0.03em] text-slate-950 sm:text-[32px]">신분증 재인증</h1>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                결제등록 흐름을 안전하게 이어가기 위해 신분증 이미지를 한 번 더 확인합니다.
              </p>
            </div>
            <div className="rounded-2xl bg-[#eef4ff] px-3 py-2 text-right">
              <div className="text-[11px] font-bold text-[#5d7bc6]">단계</div>
              <div className="text-lg font-black text-[#2f63e1]">2/3</div>
            </div>
          </div>
        </div>

        <form onSubmit={onSubmit} className="rounded-[32px] border border-[#d8e2f4] bg-white p-5 shadow-[0_20px_50px_rgba(35,76,170,0.12)] sm:p-6">
          <div className="rounded-[24px] border border-[#e7eefb] bg-[#fbfdff] p-4">
            <div className="text-[11px] font-extrabold tracking-[0.18em] text-[#5d7bc6]">업로드 안내</div>
            <p className="mt-2 text-sm leading-6 text-slate-600">인증완료 후 자동으로 이전 페이지로 돌아갑니다. JPG, PNG, WEBP 파일을 권장합니다.</p>
          </div>

          <label className="mt-4 block rounded-[24px] border border-[#d8e2f4] bg-[#f8fbff] p-4">
            <span className="mb-2 block text-sm font-semibold text-slate-800">신분증 이미지 선택</span>
            <input
              type="file"
              accept="image/*"
              onChange={onFileChange}
              className="block w-full text-sm text-slate-700 file:mr-4 file:rounded-[16px] file:border file:border-[#cfdcf5] file:bg-white file:px-4 file:py-2.5 file:text-sm file:font-semibold file:text-slate-700 hover:file:bg-[#f7faff]"
            />
            {file ? <div className="mt-3 text-xs font-semibold text-[#2f63e1]">선택된 파일: {file.name}</div> : null}
          </label>

          {err ? (
            <div className="mt-4 rounded-[18px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">{err}</div>
          ) : null}

          {message ? (
            <div className="mt-4 rounded-[18px] border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">{message}</div>
          ) : null}

          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:justify-between">
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex items-center justify-center rounded-[18px] border border-[#d1dbed] bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:bg-[#f7faff]"
            >
              이전 단계로 돌아가기
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center rounded-[18px] bg-[#2f63e1] px-6 py-3 text-sm font-extrabold text-white shadow-[0_14px_28px_rgba(47,99,225,0.25)] transition hover:bg-[#2859d1] disabled:cursor-not-allowed disabled:bg-[#b8caf5]"
            >
              {loading ? "인증 진행 중..." : "신분증 인증 요청"}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
